import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ICP {
  id: string;
  name: string;
  description: string;
  industry: string;
  company_size: string;
  job_titles: string[];
  pain_points: string[];
  goals: string[];
  budget_range: string;
  decision_makers: string[];
  misc?: any;
  created_at: string;
}

export default function ICPs() {
  const [icps, setIcps] = useState<ICP[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingIcp, setEditingIcp] = useState<ICP | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    company_size: '',
    job_titles: '',
    pain_points: '',
    goals: '',
    budget_range: '',
    decision_makers: '',
  });

  useEffect(() => {
    loadICPs();
  }, []);

  const loadICPs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/icps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setIcps(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load ICPs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (icp?: ICP) => {
    if (icp) {
      setEditingIcp(icp);
      setFormData({
        name: icp.name,
        description: icp.description,
        industry: icp.industry,
        company_size: icp.company_size,
        job_titles: icp.job_titles.join(', '),
        pain_points: icp.pain_points.join(', '),
        goals: icp.goals.join(', '),
        budget_range: icp.budget_range,
        decision_makers: icp.decision_makers.join(', '),
      });
    } else {
      setEditingIcp(null);
      setFormData({
        name: '',
        description: '',
        industry: '',
        company_size: '',
        job_titles: '',
        pain_points: '',
        goals: '',
        budget_range: '',
        decision_makers: '',
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingIcp(null);
  };

  const handleSaveICP = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        industry: formData.industry || null,
        company_size: formData.company_size || null,
        job_titles: formData.job_titles ? formData.job_titles.split(',').map(s => s.trim()).filter(Boolean) : [],
        pain_points: formData.pain_points ? formData.pain_points.split(',').map(s => s.trim()).filter(Boolean) : [],
        goals: formData.goals ? formData.goals.split(',').map(s => s.trim()).filter(Boolean) : [],
        budget_range: formData.budget_range || null,
        decision_makers: formData.decision_makers ? formData.decision_makers.split(',').map(s => s.trim()).filter(Boolean) : [],
      };

      console.log('Creating ICP with AI agent. Payload:', payload);

      if (editingIcp) {
        const response = await axios.put(`${API_URL}/icps/${editingIcp.id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        console.log('ICP updated successfully:', response.data);
        handleCloseDialog();
        await loadICPs();
      } else {
        // Step 1: Create ICP workflow via orchestrator
        const response = await axios.post(`${API_URL}/icps`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        
        const workflowId = response.data.workflow_id;
        console.log('ICP workflow created:', workflowId);
        
        // Close modal immediately (better UX!)
        handleCloseDialog();
        setError(null);
        
        // Step 2: Auto-execute workflow (ICP Strategy Agent works in background)
        await axios.post(
          `${API_URL}/workflows/${workflowId}/execute`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        
        // Show success message with agent transparency
        alert(`ICP creation started! Workflow ID: ${workflowId}\n\nICP Strategy Agent is:\n• Researching market data\n• Analyzing target segments\n• Defining customer profiles\n\nView progress in Workflows page. Refreshing ICPs in 5 seconds...`);
        
        // Reload ICPs after workflow has time to complete
        setTimeout(() => loadICPs(), 5000);
      }
    } catch (err: any) {
      console.error('ICP save error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.detail 
        || (err.response?.data?.message) 
        || (err.message)
        || 'Failed to save ICP';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteICP = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this ICP?')) return;

    try {
      await axios.delete(`${API_URL}/icps/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadICPs();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete ICP');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Ideal Customer Profiles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={window.innerWidth < 600}
        >
          Create ICP
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {icps.map((icp) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={icp.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, overflow: 'auto', maxHeight: '600px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{icp.name}</Typography>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(icp)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteICP(icp.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {icp.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Industry
                  </Typography>
                  <Typography variant="body2">{icp.industry}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Company Size
                  </Typography>
                  <Typography variant="body2">{icp.company_size}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Job Titles
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {icp.job_titles.map((title, idx) => (
                      <Chip key={idx} label={title} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Pain Points
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {icp.pain_points.map((point, idx) => (
                      <Chip key={idx} label={point} size="small" color="error" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Goals
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {icp.goals.map((goal, idx) => (
                      <Chip key={idx} label={goal} size="small" color="success" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                  </Box>
                </Box>

                {icp.misc && Object.keys(icp.misc).length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Additional Insights
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {Object.entries(icp.misc).map(([key, value]) => (
                        <Box key={key} sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {key.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, pl: 1 }}>
                            {typeof value === 'object' && value !== null ? (
                              Array.isArray(value) ? (
                                value.join(', ')
                              ) : (
                                Object.entries(value).map(([k, v]) => (
                                  <Box key={k} component="span" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>{k.replace(/_/g, ' ')}:</strong> {String(v)}
                                  </Box>
                                ))
                              )
                            ) : (
                              String(value)
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}

        {icps.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No ICPs Created Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first Ideal Customer Profile to start targeting the right audience
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Create Your First ICP
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit ICP Dialog */}
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIcp ? 'Edit ICP' : 'Create AI-Enhanced ICP'}
        </DialogTitle>
        <DialogContent>
          {!editingIcp && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Our AI Strategy Agent will research your inputs and create a comprehensive, data-driven ICP with market insights, buying signals, and qualification criteria.
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="ICP Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="e.g., Enterprise SaaS Decision Makers"
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Brief description of this ideal customer profile"
            />

            <TextField
              fullWidth
              label="Industry"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              margin="normal"
              helperText="e.g., SaaS, Healthcare, Finance"
            />

            <TextField
              fullWidth
              label="Company Size"
              value={formData.company_size}
              onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
              margin="normal"
              helperText="e.g., 50-200 employees, Enterprise (1000+)"
            />

            <TextField
              fullWidth
              label="Job Titles"
              value={formData.job_titles}
              onChange={(e) => setFormData({ ...formData, job_titles: e.target.value })}
              margin="normal"
              helperText="Comma-separated: CTO, VP Engineering, Head of Product"
            />

            <TextField
              fullWidth
              label="Pain Points"
              value={formData.pain_points}
              onChange={(e) => setFormData({ ...formData, pain_points: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Comma-separated: Slow deployment, High costs, Scalability issues"
            />

            <TextField
              fullWidth
              label="Goals"
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Comma-separated: Increase efficiency, Reduce costs, Scale operations"
            />

            <TextField
              fullWidth
              label="Budget Range"
              value={formData.budget_range}
              onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
              margin="normal"
              helperText="e.g., $10k-50k annually"
            />

            <TextField
              fullWidth
              label="Decision Makers"
              value={formData.decision_makers}
              onChange={(e) => setFormData({ ...formData, decision_makers: e.target.value })}
              margin="normal"
              helperText="Comma-separated: CTO, CFO, CEO"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveICP}
            disabled={!formData.name || !formData.industry || saving}
          >
            {saving ? (editingIcp ? 'Updating...' : 'Creating with AI...') : (editingIcp ? 'Update' : 'Create ICP')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
